import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import './UploadPage.css';
import BackgroundWrapper2 from '../components/BackgroundWrapper2';
import download from '../features/attackSurface/assets/download.png';
import { ChevronDownIcon } from '../components/Icons';
import file from '../features/attackSurface/assets/file.png';
import RiskReportInfoWrapper from '../components/RiskReportInfoWrapper';

import agentforce from '../features/attackSurface/assets/agentforce.png';
import autogen from '../features/attackSurface/assets/autogen.png';
import bedrock from '../features/attackSurface/assets/bedrock.png';
import crewai from '../features/attackSurface/assets/crewai.png';
import dify from '../features/attackSurface/assets/dify.png';
// import langgraph from '../features/attackSurface/assets/langgraph.png';
import microsoft from '../features/attackSurface/assets/microsoft.png';
import n8n from '../features/attackSurface/assets/n8n.png';
import openai from '../features/attackSurface/assets/openai.png';
import make from '../features/attackSurface/assets/make.png';
import { createPortal } from 'react-dom';
import { useValidateFrameworkMutation } from '../apiSlice';
import trashIcon from '../features/attackSurface/assets/trash.svg';

const FRAMEWORK_MAP: Record<string, string> = {
  'AutoGen': 'autogen',
  'CrewAI': 'crewai',
  'OpenAI Agents': 'openai-agents',
  // 'LangGraph': 'langgraph',
  'n8n': 'n8n',
  'Dify': 'dify',
  'Microsoft Copilot Studio': 'microsoft-copilot-studio',
  'Amazon Bedrock AgentCore': 'bedrock',
  'Agentforce': 'agentforce',
  'Make': 'make',
};

const FRAMEWORK_OPTIONS = [
  { label: 'AutoGen', value: 'AutoGen', icon: autogen },
  { label: 'CrewAI', value: 'CrewAI', icon: crewai },
  { label: 'OpenAI Agents', value: 'OpenAI Agents', icon: openai },
  // { label: 'LangGraph', value: 'LangGraph', icon: langgraph },
  { label: 'n8n', value: 'n8n', icon: n8n },
  { label: 'Dify', value: 'Dify', icon: dify },
  { label: 'Microsoft Copilot Studio', value: 'Microsoft Copilot Studio', icon: microsoft },
  { label: 'Amazon Bedrock AgentCore', value: 'Amazon Bedrock AgentCore', icon: bedrock },
  { label: 'Agentforce', value: 'Agentforce', icon: agentforce },
  {label: 'Make', value: 'Make', icon: make}
];

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState<typeof FRAMEWORK_OPTIONS[0] | null>(null);
  const [selectedAgentOption, setSelectedAgentOption] = useState<'prebuild' | 'custom'>('prebuild');
  const [selectedPrebuiltIdx, setSelectedPrebuiltIdx] = useState<number>(0);
  const [customFile, setCustomFile] = useState<File | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 0 });
  const [validateFramework] = useValidateFrameworkMutation();
  const [valited, setValited] = useState(true);
  const allowedExtensions = ['py', 'json', 'yaml', 'yml'];
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
  const [error, setError] = useState('')


  const PREBUILD_FILES: Record<string, { dir: string; file: string }[]> = {
    'AutoGen': [
      { dir: 'AutoGen', file: 'travel_planner.py' },
    ],
    'CrewAI': [
      { dir: 'CrewAI', file: 'travel_planner.py' },
    ],
    'OpenAI Agents': [
      { dir: 'OpenAI', file: 'calendar_assistant.py' },
    ],
    // 'LangGraph': [
    //   { dir: 'LangGraph', file: 'langgraph_multi_agent.py' },
    // ],
    'n8n': [
      { dir: 'n8n', file: 'CX_Orchestrator.json' },
    ],
    'Dify': [
      { dir: 'Dify', file: 'Financial_assistant.yml' },
    ],
    'Microsoft Copilot Studio': [
      { dir: 'Microsoft', file: 'enterprise_service_orchestrator.yaml' },
    ],
    'Amazon Bedrock AgentCore': [
      { dir: 'Bedrock', file: 'customer_operations.json' },
    ],
    'Agentforce': [
      { dir: 'Agentforce', file: 'enterprise_service.yaml' },
    ],
    'Make': [
      { dir: 'Make', file: 'support_orchestrator.json' },
    ]
  };

  function getPrebuildFileName() {
    if (!selectedFramework) return '';
    const mapping = PREBUILD_FILES[selectedFramework.value];
    if (!mapping) return '';
    const fileName = mapping[selectedPrebuiltIdx].file.split('.').slice(0, -1).join('.');
    return fileName + ' (prebuild)';
  }

  async function fetchPrebuiltFile(frameworkValue: string) {
    const mapping = PREBUILD_FILES[frameworkValue];
    if (!mapping) return null;

    const url = `/pre_built_files/${mapping[selectedPrebuiltIdx].dir}/${mapping[selectedPrebuiltIdx].file}`;
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new File([blob], mapping[selectedPrebuiltIdx].file, { type: blob.type });
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
          && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadPrebuiltFile = async () => {
      if (selectedFramework && selectedAgentOption === 'prebuild' && selectedPrebuiltIdx >= 0) {
        const file = await fetchPrebuiltFile(selectedFramework.value);
        if (file) {
          setSelectedFile(file);
        }
      } else if (selectedAgentOption === 'custom' && customFile) {
        setSelectedFile(customFile);
      }
    };
    loadPrebuiltFile();
  }, [selectedFramework, selectedAgentOption, selectedPrebuiltIdx]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const ext = file.name.split('.').pop()?.toLowerCase();
      setCustomFile(file); 
      setSelectedFile(file);
      setSelectedAgentOption('custom');

      if(!(ext && allowedExtensions.includes(ext))){
        setError('Unsupported file type. Please upload a .py, .json, .yaml, or .yml file.');
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError('File size exceeds 100 MB. Please select a smaller file.');
        return;
      }

      setError('');
    }
  };

  const handleRemoveFile = () => {
    setCustomFile(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setError('');
  };

  const handleScan = async () => {
    if (selectedFramework && selectedFile) {
      const ext = selectedFile.name.split('.').pop()?.toLowerCase();
      if(!(ext && allowedExtensions.includes(ext))){
        setError('Unsupported file type. Please upload a .py, .json, .yaml, or .yml file.');
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE) {
        setError('File size exceeds 100 MB. Please select a smaller file.');
        return;
      }
      
      setValited(false);
      const result = await validateFramework({ framework: FRAMEWORK_MAP[selectedFramework.value], file: selectedFile }).unwrap();
      setValited(true);
      if (!result.valid) {
        setError("Framework does not match the file. Please double check.");
        return;
      }

      setError('');
      navigate('/scanning', { 
        state: { 
          framework: FRAMEWORK_MAP[selectedFramework.value], 
          file: selectedFile, 
          fileExt: ext,
          fileName: selectedFile.name, 
        } 
      });
    } else {
      setError('Please select both framework and file');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const ext = file.name.split('.').pop()?.toLowerCase();
      setCustomFile(file);
      setSelectedFile(file);
      setSelectedAgentOption('custom');

      if(!(ext && allowedExtensions.includes(ext))){
        setError('Unsupported file type. Please upload a .py, .json, .yaml, or .yml file.');
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError('File size exceeds 100 MB. Please select a smaller file.');
        return;
      }

      setError('');
    }
  };

  const handleDropdownOpen = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPos({ left: rect.left, top: rect.bottom, width: rect.width });
    }
    setIsOpen(true);
  };

  return (
    <BackgroundWrapper2>
      <div className="upload-container">
        <Header />

        <main className="upload-main">
          {/* Left side */}
          <RiskReportInfoWrapper />

          {/* Right side - Upload form */}
          <div className="upload-right">
            <div className="upload-form-section">
              <label className="upload-label">
                <span className="upload-label-required">*</span> Choose Framework
                </label>
              
              <div className="custom-dropdown" ref={dropdownRef}>
                <button
                  type="button"
                  className="dropdown-trigger"
                  onClick={handleDropdownOpen}
                >
                  {selectedFramework ? (
                    <>
                      <img src={selectedFramework.icon} alt="" className="dropdown-icon" />
                      <span>{selectedFramework.label}</span>
                    </>
                  ) : (
                    <span className="placeholder">Choose Framework</span>
                  )}
                  <ChevronDownIcon size={22} color="#fff" />
                </button>

                {isOpen &&
                  createPortal(
                    <div
                      ref={menuRef}
                      className="dropdown-menu"
                      style={{
                        position: 'absolute',
                        left: dropdownPos.left,
                        top: dropdownPos.top,
                        width: dropdownPos.width,
                        zIndex: 9999,
                      }}
                    >
                      {FRAMEWORK_OPTIONS.map((option) => (
                        <div
                          key={option.value}
                          className="dropdown-option"
                          onClick={() => {
                            setSelectedFramework(option);
                            setIsOpen(false);
                            setError('');
                          }}
                        >
                          <img src={option.icon} alt="" className="dropdown-icon" />
                          <span>{option.label}</span>
                        </div>
                      ))}
                    </div>,
                    document.body
                  )}
              </div>
            </div>

            <div className="upload-form-section">
              <label className="upload-label">
                <span className="upload-label-required">*</span> Choose Agent
                </label>
              
              {selectedFramework ? 
                (
                  <>
                    <div className="agent-bullet-options" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {PREBUILD_FILES[selectedFramework.value]?.map((item, idx) => (
                        <label className="agent-bullet" key={item.file}>
                          <input
                            type="radio"
                            name="agent-option"
                            value={`prebuild-${idx}`}
                            checked={selectedAgentOption === 'prebuild' && selectedPrebuiltIdx === idx}
                            onChange={() => {
                              setSelectedAgentOption('prebuild');
                              setSelectedPrebuiltIdx(idx);
                              setError('');
                            }}
                          />
                          <span>{(item.file.split('.').slice(0, -1).join('.')).replaceAll("_", " ")} (example)</span>
                        </label>
                      ))}
                      <label className="agent-bullet">
                        <input
                          type="radio"
                          name="agent-option"
                          value="custom"
                          checked={selectedAgentOption === 'custom'}
                          onChange={() => {
                            setSelectedAgentOption('custom');
                            setSelectedPrebuiltIdx(-1);
                            setSelectedFile(customFile);
                          }}
                        />
                        <span>Upload custom</span>
                        <br></br>
                        <span className="upload-custom-note">
                          Note: please remove any security identifiers from the file.
                        </span>
                      </label>
                    </div>

                    {selectedFramework && (
                      <>
                        <div
                          className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          {/* <img src={download} alt="" className="download-icon" /> */}
                          <p className={customFile ? "upload-dropzone-text-disabled" : "upload-dropzone-text"}>
                            Drag & Drop the file
                          </p>
                          <p className="upload-dropzone-or">or</p>
                          <label
                            className={`upload-file-select${customFile ? ' upload-file-select-disabled' : ''}`}
                            aria-disabled={customFile ? 'true' : 'false'}
                          >
                            Choose file
                            <input
                              type="file"
                              ref={fileInputRef}
                              className="upload-file-input"
                              accept={allowedExtensions.map(ext => '.' + ext).join(',')}
                              disabled={customFile !== null}
                              onChange={handleFileSelect}
                            />
                          </label>
                        </div>
                        <p className="upload-file-size">
                          Only single file flows are supported. 
                          <br/>
                          Supported formats: .json, .yaml or .py. Max: 100 MB
                        </p>
                      </>
                    )}
                  </>
                ) : 
                (
                  <p className="upload-subtitle">
                    Select framework first
                  </p>
                )
              }

            </div>

            {customFile && (
              <div className="upload-file-info">
                <div>
                  <img src={file} alt="" className="file-icon" />
                  <div className="upload-file-meta">
                    <div className="upload-file-name">{customFile.name}</div>
                    {error ? 
                      (<p className="error-message">{error}</p>) : 
                      <div className="upload-file-size-info">
                      {(customFile.size / 1024 / 1024).toFixed(5)} MB
                    </div>
                    }  
                  </div>
                </div>
                <button onClick={handleRemoveFile} className="upload-remove-button">
                  <img src={trashIcon} alt=""/>
                </button>
              </div>
            )}
            <button
              onClick={handleScan}
              className="upload-submit-button"
              disabled={!(selectedFramework && selectedFile) || !valited || error != ''}
            >
              Scan for Free
            </button>
          </div>
        </main>
      </div>
    </BackgroundWrapper2>
  );
};

export default UploadPage;